import type { KspPublishedSnapshot } from '@entities/ksp/types';
import { decimal, kpiLabel, label, projectDate } from '@entities/ksp/format';
import { Card, Chip, Grid, Muted, Notice, Small, Table, TableBox } from './parts';

/** Сколько источников стоит за цифрой: сами ссылки (хеши) — в подсказке, не на экране. */
function SourceCount({
  refs,
  data,
}: {
  refs: string[];
  data: Pick<
    KspPublishedSnapshot,
    | 'source_ref_count'
    | 'source_ref_count_kind'
    | 'ledger_record_count'
    | 'schedule_source_ref_count'
  >;
}) {
  const total = data.source_ref_count ?? refs.length;
  if (!total && !refs.length) return null;
  const detail =
    data.source_ref_count_kind === 'distinct_accepted_sources'
      ? `Источников принятого факта: ${data.source_ref_count ?? '—'} · записей реестра: ${data.ledger_record_count ?? '—'} · ссылок графика: ${data.schedule_source_ref_count ?? '—'}`
      : `Источников: ${total}`;
  return (
    <Small title={[detail, ...refs].join('\n')}>
      {detail}. Полный состав — в снимке расчёта и его выгрузке.
    </Small>
  );
}

const statusTone = (status: string) =>
  status === 'ok' ? 'ok' : status === 'unavailable' ? 'neutral' : 'warn';

export function KspSummary({ data }: { data: KspPublishedSnapshot | null }) {
  if (!data)
    return (
      <Notice>
        Опубликованного расчёта ещё нет. Проверьте исходные данные, рассчитайте график и опубликуйте
        расчёт — тогда здесь появятся показатели.
      </Notice>
    );
  const deviation = data.finish_deviation_calendar_days;
  return (
    <Card>
      <h3>Показатели опубликованного расчёта</h3>
      <Muted title={`Расчёт ${projectDate(data.calculated_at, data.timezone)}`}>
        Дата состояния {projectDate(data.data_date, data.timezone)} · {label(data.coverage)} · расчёт{' '}
        {projectDate(data.calculated_at, data.timezone)}
        {data.quality.stale ? ' · данные устарели' : ''}
      </Muted>
      <Grid>
        {data.kpis.map((kpi) => {
          const reasons = kpi.reasons ?? (kpi.reason ? [kpi.reason] : []);
          return (
            <Card key={kpi.code}>
              <strong>{kpiLabel(kpi.code)}</strong>
              <h3>{decimal(kpi.fact_pct, ' %')}</h3>
              <Muted>
                План {decimal(kpi.plan_pct, ' %')} · отклонение {decimal(kpi.delta_pp, ' п.п.')}
              </Muted>
              <Chip $tone={statusTone(kpi.status)}>{label(kpi.status)}</Chip>
              <Small>
                {label(kpi.scope_kind)} · {label(kpi.method)}
              </Small>
              {reasons.length > 0 && <Small>{reasons.map(label).join(' · ')}</Small>}
              <SourceCount refs={kpi.source_refs} data={kpi} />
            </Card>
          );
        })}
      </Grid>
      <TableBox>
        <Table>
          <tbody>
            <tr>
              <th>Договорное окончание</th>
              <td>{projectDate(data.contract_finish, data.timezone)}</td>
              <th>Прогноз окончания</th>
              <td>{projectDate(data.forecast_finish, data.timezone)}</td>
            </tr>
            <tr>
              <th>Отклонение от договора</th>
              <td>
                {deviation == null
                  ? '—'
                  : deviation < 0
                    ? `раньше на ${Math.abs(deviation)} дн.`
                    : deviation > 0
                      ? `позже на ${deviation} дн.`
                      : 'в срок'}
              </td>
              <th>Открытые предупреждения по вехам</th>
              <td>{data.open_milestone_alert_count}</td>
            </tr>
          </tbody>
        </Table>
      </TableBox>
      {data.quality.reasons.length > 0 && (
        <Notice>{data.quality.reasons.map(label).join('\n')}</Notice>
      )}
      <SourceCount refs={data.source_refs ?? []} data={data} />
      <Small title={`Снимок ${data.snapshot_id} · версия входов ${data.input_version}`}>
        {data.quality.stale ? 'Данные устарели — рассчитайте заново' : 'Согласованный срез'}
        {data.operational_as_of
          ? ` · оперативные данные на ${projectDate(data.operational_as_of, data.timezone)}`
          : ''}
      </Small>
    </Card>
  );
}
